import React from "react";
import {
  OutlinedInput,
  Stack,
  Typography,
  Divider,
  Button,
  Autocomplete,
  Switch,
  FormControl,
  InputLabel,
  Grid,
  Card,
  FormControlLabel,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import UserGroupChip from "../../Misc/Chips/UserGroupChip/user-group-chip";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { orderService, userService } from "../../../Core/Api/services";
import { produce } from "immer";
import { ModalContext } from "../../Modals";
import { GroupAdvancedInfos } from "../../../Core/Api/Types/group-advanced-infos";
import { DbCustomerInfos } from "../../../Core/Api/Types/db-customer-infos";
import { UserAdvancedInfos } from "../../../Core/Api/Types/user-advanced-infos";
import { UserHasAcl } from "../../../Utils/helpers";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import ModalAddGroup, { ModalAddGroupElementProps } from "../../Modals/ModalAddGroup/modal-add-group";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import LockResetIcon from "@mui/icons-material/LockReset";
import dayjs from "dayjs";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { useTranslation } from "react-i18next";

export interface CustomerEditUserPanelProps {
  width?: number;
  open: boolean;
  user?: UserAdvancedInfos;
  onClose?: React.MouseEventHandler;
}

const CustomerEditUserPanel: React.FC<CustomerEditUserPanelProps> = (props) => {
  const { t } = useTranslation();
  const [userModified, setUserModified] = React.useState<UserAdvancedInfos>(new UserAdvancedInfos());
  const [groupList, setGroupList] = React.useState<GroupAdvancedInfos[]>([]);
  const [roleList, setRoleList] = React.useState<DbUserGroupRoleInfos[]>([]);
  const [groupSelected, setGroupSelected] = React.useState<GroupAdvancedInfos>();
  const [customers, setCustomers] = React.useState<DbCustomerInfos[]>([]);
  const [confirmPassword, setConfirmPassword] = React.useState<string | null>(null);

  const enqueueCustomSnackbar = useCustomSnackbar();

  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const handleOpenModalNewGroup = React.useCallback(() => {
    if (!openModal) return;
    const onClose: ModalAddGroupElementProps["onClose"] = (data) => {};

    openModal<ModalAddGroupElementProps>(ModalAddGroup, {
      onClose,
    });
  }, [openModal]);

  const handleDeleteClick = () => {
    if (userModified.userInfos) {
      userService.DeleteUser(userModified.userInfos.id).then((response) => {
        if (response && response.success) enqueueCustomSnackbar(t("alert:success.userDeleted"), "success");
      });
    }
  };

  const handleResetPasswordClick = () => {
    if (userModified.userInfos) {
      userService
        .ForgotPasswordRequest({
          login: userModified.userInfos.login,
        })
        .then((response) => {
          if (response && response.success)
            enqueueCustomSnackbar(t("alert:success.changePasswordEmailSent"), "success");
        });
    }
  };

  const handleResendClick = () => {
    if (userModified.userInfos) {
      userService.ResendVerifyEmail(userModified.userInfos.id).then((response) => {
        if (response && response.success) enqueueCustomSnackbar(t("alert:success.newVerificationEmailSent"), "success");
      });
    }
  };

  const handleSubmit = () => {
    if (userModified)
      userService.UpdateUser(userModified).then(function (response) {
        if (response.success) enqueueCustomSnackbar(t("alert:success.modifiedUser"), "success");
      });
  };

  React.useEffect(() => {
    if (props.user) {
      setUserModified(props.user);
      setGroupSelected(groupList.find((x) => x.id === props.user!.userInfos!.groupId));
    }
  }, [props.user, groupList]);

  React.useEffect(() => {
    userService.GetGroupsList().then(function (response) {
      if (response.success && response.groups) {
        setGroupList(response.groups);
      }
    });

    orderService.GetCustomersList().then(function (response) {
      if (response.success) setCustomers(response.customers || []);
    });

    userService.GetGroupRolesList().then(function (response) {
      if (response.success) {
        setRoleList(response.groupRoles || []);
      }
    });
  }, []);

  return (
    <>
      {userModified.userInfos && (
        <Panel
          header={<PanelHeader title={t("common:editUser")} onClose={props.onClose as PanelHeaderProps["onClose"]} />}
          footerContent={
            <Button color="primary" variant="contained" fullWidth onClick={handleSubmit}>
              {t("button:confirmChanges")}
            </Button>
          }
          reload={userModified.userInfos.id}
          open={props.open}
          onClose={props.onClose}
        >
          <Stack spacing={1.5}>
            <Typography variant="h5" mb={2}>
              {t("common:generalInformations")}
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="firstname">
                {t("common:firstName")}
              </InputLabel>
              <OutlinedInput
                id="firstname"
                value={userModified.userInfos!.firstName || ""}
                onChange={(e) => {
                  setUserModified(
                    produce((draft) => {
                      draft.userInfos!.firstName = e.target.value;
                    })
                  );
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="lastname">
                {t("common:lastName")}
              </InputLabel>
              <OutlinedInput
                id="lastname"
                value={userModified.userInfos!.lastName || ""}
                onChange={(e) => {
                  setUserModified(
                    produce((draft) => {
                      draft.userInfos!.lastName = e.target.value;
                    })
                  );
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="email">
                {t("common:emailAddress")}
              </InputLabel>
              <OutlinedInput
                id="email"
                value={userModified.userInfos!.email || ""}
                onChange={(e) => {
                  setUserModified(
                    produce((draft) => {
                      draft.userInfos!.email = e.target.value;
                    })
                  );
                }}
              />
            </FormControl>
            <FormControl sx={{ width: "100%" }}>
              <InputLabel shrink htmlFor="phonenumber">
                {t("common:phoneNumber")}
              </InputLabel>
              <OutlinedInput
                id="phonenumber"
                value={userModified.userInfos!.phone || ""}
                onChange={(e) => {
                  setUserModified(
                    produce((draft) => {
                      draft.userInfos!.phone = e.target.value;
                    })
                  );
                }}
              />
            </FormControl>
          </Stack>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Stack spacing={1.5}>
            <Typography variant="h5">{t("modal:body.customerLinks")}</Typography>
            <Typography mt={0.5} color="text.tier">
              {t("panel:customerEditUserPanel.canAssociateAnAccount")}
            </Typography>

            <Stack direction="row" alignItems="center">
              {customers && (
                <Autocomplete
                  value={userModified.userCustomersLinks || []}
                  multiple
                  onChange={(e, value) =>
                    setUserModified(
                      produce((draft) => {
                        draft.userCustomersLinks = value;
                      })
                    )
                  }
                  getOptionLabel={(option) => "#" + option.id.toString() + " " + option.name || ""}
                  options={customers}
                  sx={{ width: "100%" }}
                  id={"customerLinks"}
                  renderInput={(params) => <TextField sx={{ mt: "-24px" }} {...params} />}
                  isOptionEqualToValue={(option) =>
                    userModified.userCustomersLinks?.flatMap((x) => x.id).includes(option.id) || false
                  }
                />
              )}
            </Stack>
          </Stack>
          <Divider sx={{ mt: 3, mb: 3 }} />
          <Stack spacing={1.5}>
            <Typography variant="h5">{t("field:label.assignedTo")}</Typography>
            <Typography mt={0.5} color="text.tier">
              {t("panel:customerEditUserPanel.userCanInheritAllPermission")}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {groupList && (
                <Autocomplete
                  disablePortal
                  disableClearable={true}
                  options={groupList}
                  renderTags={() => null} // If is controlled, render tags below
                  sx={{ flex: 1 }}
                  popupIcon={<ExpandMoreRoundedIcon />}
                  getOptionLabel={(option) => option.group?.name || ""}
                  onChange={(e, value) => {
                    setGroupSelected(value ? value : undefined);
                    setUserModified(
                      produce((draft) => {
                        draft.userInfos!.groupId = value?.id!;
                      })
                    );
                  }}
                  value={groupList.find((x) => x.id === userModified.userInfos?.groupId)}
                  renderInput={(params) => (
                    <OutlinedInput
                      {...params.InputProps}
                      inputProps={params.inputProps}
                      placeholder={t("field:placeholder.searchExistingGroup")}
                      sx={{ width: "100%" }}
                    />
                  )}
                />
              )}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenModalNewGroup}
                disabled={!UserHasAcl(AclTypes.aclGroupCreate)}
              >
                {t("common:newGroup")}
              </Button>
            </Stack>

            {groupSelected && (
              <Stack direction="row" flexWrap="wrap">
                <UserGroupChip roleId={groupSelected.group?.roleId} roles={roleList} sx={{ mr: 1, mb: 1 }} />
              </Stack>
            )}
          </Stack>

          {/*<Divider sx={{ mt: 3, mb: 3 }} /><Stack spacing={1.5}>
            <Typography variant="h5">Permission exceptions</Typography>
            <OutlinedInput
              label="Search for a permission"
              sx={{ width: "100%", mt: 2 }}
              placeholder="Search for a permission"
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              }
            />
            <Box>
              {["Create Requests", "Monitor Request", "Assign Requests", "Delete Requests"].map((permission) => (
                <ListItemCard key={permission}>
                  <Typography fontWeight={500}>{permission}</Typography>

                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Tooltip title="Tooltip Content">
                      <ErrorOutlineOutlinedIcon sx={{ color: "text.tier" }} fontSize="small" />
                    </Tooltip>
                    <Switch size="small" />
                    <IconButton color="error">
                      <DeleteOutlineOutlinedIcon fontSize="small" color="error" />
                    </IconButton>
                  </Stack>
                </ListItemCard>
              ))}
            </Box>
              </Stack>*/}

          <Divider sx={{ mt: 3, mb: 3 }} />
          <Stack spacing={1.5}>
            <Typography variant="h5" mb={2}>
              {t("common:security")}
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel shrink htmlFor="password">
                    {t("common:password")}
                  </InputLabel>
                  <OutlinedInput
                    id="password"
                    value={userModified.userInfos!.password || ""}
                    onChange={(e) => {
                      setUserModified(
                        produce((draft) => {
                          draft.userInfos!.password = e.target.value;
                        })
                      );
                    }}
                  />
                </FormControl>
              </Grid>
              <Grid item md={6}>
                <FormControl sx={{ width: "100%" }}>
                  <InputLabel shrink htmlFor="confirmpassword">
                    {t("field:label.confirmPassword")}
                  </InputLabel>
                  <OutlinedInput
                    id="confirmpassword"
                    value={confirmPassword || ""}
                    error={confirmPassword !== userModified.userInfos.password}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Card variant="outlined" sx={{ p: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    onChange={(e) => {
                      setUserModified(
                        produce((draft) => {
                          draft.userInfos!.enabled = e.target.checked;
                        })
                      );
                    }}
                    checked={userModified.userInfos!.enabled || false}
                  />
                }
                labelPlacement="start"
                sx={{ ml: 0, justifyContent: "flex-end", width: "100%", ".MuiFormControlLabel-label": { flex: 1 } }}
                label={t("field:label.enableUser")}
              />
              <Divider sx={{ mt: 2, mb: 2 }} />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label={t("common:expireDate")}
                  sx={{ width: "100%" }}
                  disabled={!userModified.userInfos!.enabled || userModified.userInfos!.enabled === null}
                  value={dayjs(userModified.userInfos?.expireDate)}
                  format={GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS}
                  onChange={(e) => {
                    setUserModified(
                      produce((draft) => {
                        draft.userInfos!.expireDate = dayjs(e).toDate();
                      })
                    );
                  }}
                />
              </LocalizationProvider>
              {userModified.userInfos!.expireDate !== null && (
                <Button
                  variant="text"
                  size="small"
                  sx={{ mt: 1 }}
                  onClick={() =>
                    setUserModified(
                      produce((draft) => {
                        draft.userInfos!.expireDate = null;
                      })
                    )
                  }
                >
                  {t("button:clearExpiration")}
                </Button>
              )}
            </Card>

            <Button
              color="error"
              startIcon={<DeleteOutlineOutlinedIcon />}
              onClick={handleDeleteClick}
              disabled={!UserHasAcl(AclTypes.aclBackofficeUserDelete)}
            >
              {t("button:deleteUser")}
            </Button>
            <Button color="primary" variant="outlined" startIcon={<LockResetIcon />} onClick={handleResetPasswordClick}>
              {t("button:sendResetPasswordEmail")}
            </Button>
            {userModified.userInfos.isVerified === false && (
              <Button color="primary" variant="outlined" startIcon={<LockResetIcon />} onClick={handleResendClick}>
                {t("button:resendVerifyEmail")}
              </Button>
            )}
          </Stack>
        </Panel>
      )}
    </>
  );
};

export default CustomerEditUserPanel;
